import { IListStandaloneInvoiceDto } from '../../types/INewListInvoicesResponse';
import { IStandaloneInvoiceItemPreviewDto, IStandaloneInvoicePreviewDto, ITaxDto } from './IStandaloneInvoicePreview';
import {
    IInvoiceItemOrderRelationViewDto,
    IInvoiceItemReceiptRelationViewDto,
} from '../../detail/types/IStandaloneInvoiceDetail';
import { SortingService } from '@lib/util';
import { InvoiceStatus } from '@lib/invoice-public';
import { InvoiceDocumentType } from '../../types/InvoiceDocumentType';

export class ListInvoice {
    id: string;
    bookingDate: string;
    collectiveInvoiceNumber: string;
    currency: string | null;
    documentDate: string;
    invoiceNumber: string;
    items: IStandaloneInvoiceItemPreviewDto[];
    note: string;
    status: InvoiceStatus;
    documentType: InvoiceDocumentType;
    isCreditNote: boolean;
    supplierName: string;
    supplierNumber: string;
    netAmount: number | null;
    grossAmount: number | null;
    taxes: ITaxDto[];
    numberOfFiles: number;
    linkedOrders: number;
    paymentUntil: string;
    paymentDate: string;
    createDate: string;

    private readonly _fullyLoaded: boolean;

    get fullyLoaded(): boolean {
        return this._fullyLoaded;
    }

    public get orderRelations(): IInvoiceItemOrderRelationViewDto[] {
        const relationMap = new Map<string, IInvoiceItemOrderRelationViewDto>();
        this.items
            .filter((item) => item.orderRelation !== undefined)
            .forEach((i) => {
                if (!relationMap.has(i.orderRelation.orderId)) {
                    relationMap.set(i.orderRelation.orderId, i.orderRelation);
                }
            });

        return SortingService.sort(Array.from(relationMap.values()), (relation) => relation.orderId);
    }

    public get receiptRelations(): IInvoiceItemReceiptRelationViewDto[] {
        const relationMap = new Map<number, IInvoiceItemReceiptRelationViewDto>();
        this.items
            .filter((item) => item.receiptRelation !== undefined)
            .forEach((i) => {
                if (!relationMap.has(i.receiptRelation.receiptId)) {
                    relationMap.set(i.receiptRelation.receiptId, i.receiptRelation);
                }
            });

        return SortingService.sort(Array.from(relationMap.values()), (relation) => relation.receiptId);
    }

    constructor(dto: IListStandaloneInvoiceDto, isFullyLoaded = false) {
        this.id = dto.id;
        this.documentDate = dto.invoiceDate;
        this.bookingDate = dto.createDate;
        this.supplierName = dto.supplierName;
        this.supplierNumber = dto.supplierNumber ?? '';
        this.invoiceNumber = dto.invoiceNumber;
        this.status = dto.status;
        // A missing type reads as an ordinary invoice
        this.documentType = dto.documentType ?? InvoiceDocumentType.INVOICE;
        this.isCreditNote = this.documentType === InvoiceDocumentType.CREDIT_NOTE;
        this.netAmount = dto.netPrice?.amount ?? null;
        this.currency = dto.netPrice?.currency ?? null;
        this.items = [];
        this.numberOfFiles = dto.numberOfFiles;
        this.linkedOrders = dto.linkedOrders;
        this._fullyLoaded = isFullyLoaded;
    }

    public applyPreviewData(dto: IStandaloneInvoicePreviewDto): ListInvoice {
        const newInstance = new ListInvoice(
            {
                id: this.id,
                createDate: this.bookingDate,
                supplierName: this.supplierName,
                supplierNumber: this.supplierNumber,
                invoiceNumber: this.invoiceNumber,
                status: this.status,
                documentType: this.documentType,
                netPrice: this.netAmount !== null && this.currency !== null 
                    ? { amount: this.netAmount, currency: this.currency }
                    : null,
                numberOfFiles: this.numberOfFiles,
                linkedOrders: this.linkedOrders,
                invoiceDate: this.documentDate,
            },
            true,
        );

        newInstance.collectiveInvoiceNumber = dto.collectiveInvoiceNumber;
        newInstance.items = dto.items;
        newInstance.note = dto.remark;
        newInstance.grossAmount = dto.grossAmount?.amount ?? null;
        newInstance.taxes = dto.taxes;
        newInstance.paymentUntil = dto.paymentUntil;
        newInstance.paymentDate = dto.paymentDate;

        return newInstance;
    }
}
